import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Accordion,
  useFormField,
  Text,
  ChecklistField,
  Button,
  FieldChangeEvent,
  styles,
  useModalControl,
} from '@frontend/design-system';
import { useLocationPickerDemoUiEnabled } from '../demo-ui-config';
import { useLocationPicker } from '../store';
import { LocationPickerTestingIds } from '../testing-ids';
import { LocationPickerTrackingIds } from '../tracking-ids';
import { ViewDemoSetupModalController } from './demo-components';
import { DemoExpirationChip } from './demo-expiration-chip';
import { LocationRadioField } from './shared/location-radio';

interface LocationGroupSectionProps {
  children: React.ReactNode;
  groupId: string;
  groupLocations: string[];
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  allowCrossGroupSelection?: boolean;
}

interface LocationGroupContentProps {
  groupId: string;
  groupLocations: string[];
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  allowCrossGroupSelection?: boolean;
}

type LocationGroupSectionSimpleViewProps = LocationGroupContentProps & {
  currentOrgId: string;
};

export const LocationGroupContent = ({
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
  allowCrossGroupSelection = false,
}: LocationGroupContentProps) => {
  const {
    inFlightLocationIds,
    setInFlightGroupId,
    setInFlightLocationIds,
    setInFlightOrganizationId,
    groupViewId,
    organizationViewId,
    setGroupViewId,
    inFlightGroupId,
    hasSingleLocationGroup,
  } = useLocationPicker([
    'inFlightLocationIds',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'groupViewId',
    'organizationViewId',
    'setGroupViewId',
    'inFlightGroupId',
    'hasSingleLocationGroup',
  ]);

  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);

  const checklistField = useFormField(
    {
      type: 'checklist',
      value: allowCrossGroupSelection
        ? groupLocations.filter((id) => inFlightSet.has(id))
        : groupViewId === groupId
        ? inFlightLocationIds
        : [],
    },
    [allowCrossGroupSelection, groupLocations, inFlightSet, groupViewId, groupId]
  );

  const selectedGroupLocations = checklistField.value;

  const handleChecklistChange = useCallback(
    (e?: FieldChangeEvent<string>) => {
      if (!e || !('value' in e)) {
        return;
      }

      setInFlightOrganizationId(organizationViewId ?? '');
      setGroupViewId(groupId);
      const newValue = e.value;
      let nextLocations: string[];
      if (allowCrossGroupSelection) {
        const selectedLocationsInGroup = groupLocations.filter((locationId) =>
          inFlightLocationIds.includes(locationId)
        );
        nextLocations = selectedLocationsInGroup.includes(newValue)
          ? selectedLocationsInGroup.filter((locationId) => locationId !== newValue)
          : [...selectedLocationsInGroup, newValue];
      } else if (inFlightGroupId !== groupId) {
        nextLocations = [newValue];
      } else {
        nextLocations = inFlightLocationIds.includes(newValue)
          ? inFlightLocationIds.filter((id) => id !== newValue)
          : [...inFlightLocationIds, newValue];
      }
      setInFlightLocationIds(nextLocations);
      setInFlightGroupId(nextLocations.length === 0 ? '' : groupId);
    },
    [
      allowCrossGroupSelection,
      groupId,
      inFlightGroupId,
      inFlightLocationIds,
      groupLocations,
      organizationViewId,
      setGroupViewId,
      setInFlightGroupId,
      setInFlightLocationIds,
      setInFlightOrganizationId,
    ]
  );

  if (groupLocations.length === 0) {
    return <LocationGroupEmptyContent />;
  }

  return (
    <>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        <Text size={hasSingleLocationGroup ? 'large' : 'medium'} weight='bold'>
          Locations
        </Text>
        <SelectDeselect
          selectedGroupLocations={selectedGroupLocations}
          groupLocations={groupLocations}
          groupId={groupId}
          allowCrossGroupSelection={allowCrossGroupSelection}
        />
      </div>
      <ChecklistField
        {...checklistField}
        css={css`
          min-width: 0;
        `}
        onChange={handleChecklistChange}
        label=''
        name='locationCheckList'
      >
        {groupLocations.map((location) => (
          <ChecklistField.Option
            css={css`
              > label {
                min-width: 0;
              }
            `}
            key={location}
            name={location}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
                width: 100%;
                min-width: 0;
              `}
            >
              <span css={[styles.truncate]}>{getScopeName(location, 'location')} </span>
              <span>{renderLocationInfo?.(location)}</span>
            </span>
          </ChecklistField.Option>
        ))}
      </ChecklistField>
    </>
  );
};

const LocationGroupEmptyContent = () => {
  const { t } = useTranslation('location-picker');

  return (
    <Text size='medium' color='subdued'>
      <Trans t={t}>
        This location group contains locations that you are not authorized to access. To manage your organization's
        settings, click <strong>'Apply'</strong>.
      </Trans>
    </Text>
  );
};

export const LocationGroupSectionSimpleView = ({
  currentOrgId,
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
  allowCrossGroupSelection = false,
}: LocationGroupSectionSimpleViewProps) => {
  const { t } = useTranslation('location-picker');
  const { hasSingleLocationGroup } = useLocationPicker(['hasSingleLocationGroup']);
  const { modalProps, triggerProps } = useModalControl();
  const showDemoUi = useLocationPickerDemoUiEnabled();

  return !hasSingleLocationGroup ? (
    <>
      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',

          marginBottom: 'var(--fabric-space-gap-md)',
        }}
      >
        <Text size='large' weight='bold'>
          {t('Location Groups')}
        </Text>
        {showDemoUi ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
            <DemoExpirationChip orgId={currentOrgId ?? ''} />
            {currentOrgId && (
              <Button {...triggerProps} variant='tertiary' iconName='note' size='small'>
                {t('View Demo Setup')}
              </Button>
            )}
          </div>
        ) : null}
      </section>
      {showDemoUi ? <ViewDemoSetupModalController {...modalProps} orgId={currentOrgId ?? ''} /> : null}
      <article
        css={css`
          border: 1px solid ${theme.colors.neutral20};
          border-radius: ${theme.borderRadius.medium};
          overflow: hidden;
        `}
      >
        <header
          css={css`
            padding: ${theme.spacing(2)};
            background-color: ${theme.colors.neutral5};
          `}
        >
          <Text as='h6' size='large' weight='bold'>
            {getScopeName(groupId, 'group')}
          </Text>
        </header>
        <section
          data-testid='location-picker-group-section'
          css={css`
            padding: ${theme.spacing(2)};
          `}
        >
          <LocationGroupContent
            renderLocationInfo={renderLocationInfo}
            groupId={groupId}
            groupLocations={groupLocations}
            getScopeName={getScopeName}
            allowCrossGroupSelection={allowCrossGroupSelection}
          />
        </section>
      </article>
    </>
  ) : (
    <section
      css={css`
        padding: ${theme.spacing(1)};
      `}
    >
      <LocationGroupContent
        renderLocationInfo={renderLocationInfo}
        groupId={groupId}
        groupLocations={groupLocations}
        getScopeName={getScopeName}
        allowCrossGroupSelection={allowCrossGroupSelection}
      />
    </section>
  );
};

export const LocationGroupSection = ({
  children,
  groupId,
  groupLocations,
  getScopeName,
  renderLocationInfo,
  allowCrossGroupSelection = false,
}: LocationGroupSectionProps) => {
  const {
    inFlightGroupId,
    inFlightLocationIds,
    setInFlightGroupId,
    setInFlightLocationIds,
    setGroupViewId,
    organizationViewId,
    setInFlightOrganizationId,
  } = useLocationPicker([
    'inFlightGroupId',
    'inFlightLocationIds',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'setGroupViewId',
    'organizationViewId',
  ]);

  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);
  const hasSelectedLocationInGroup = groupLocations.some((id) => inFlightSet.has(id));
  const isGroupSelected = allowCrossGroupSelection ? hasSelectedLocationInGroup : inFlightGroupId === groupId;

  const handleRadioSelected = useCallback(() => {
    if (groupLocations.length === 0) return;

    setInFlightOrganizationId(organizationViewId ?? '');
    setGroupViewId(groupId);

    if (allowCrossGroupSelection) {
      const selectedLocationsInGroup = groupLocations.filter((locationId) => inFlightSet.has(locationId));
      const nextLocations = hasSelectedLocationInGroup ? selectedLocationsInGroup : groupLocations;

      setInFlightLocationIds(nextLocations);
      setInFlightGroupId(nextLocations.length === 0 ? '' : groupId);
      return;
    }

    if (inFlightGroupId === groupId) {
      return;
    }

    setInFlightGroupId(groupId);
    setInFlightLocationIds(groupLocations);
  }, [
    allowCrossGroupSelection,
    groupId,
    groupLocations,
    hasSelectedLocationInGroup,
    inFlightGroupId,
    inFlightSet,
    organizationViewId,
    setGroupViewId,
    setInFlightGroupId,
    setInFlightLocationIds,
    setInFlightOrganizationId,
  ]);

  return (
    <div>
      <Accordion.Item value={groupId} title='Location Group'>
        <Accordion.Header>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(1)};
            `}
          >
            <LocationRadioField
              testId={LocationPickerTestingIds.groupRadio(groupId)}
              isSelected={isGroupSelected}
              id={`location-group-section-${groupId}`}
              onSelected={handleRadioSelected}
              trackingId={LocationPickerTrackingIds.mainGroup}
            />
            <Text weight='bold' size='large' as='h6' color={isGroupSelected ? 'primary' : 'default'}>
              {children}
            </Text>
            <LocationsSelectedCount groupLocations={groupLocations} />
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <LocationGroupContent
            renderLocationInfo={renderLocationInfo}
            groupId={groupId}
            groupLocations={groupLocations}
            getScopeName={getScopeName}
            allowCrossGroupSelection={allowCrossGroupSelection}
          />
        </Accordion.Body>
      </Accordion.Item>
    </div>
  );
};

const LocationsSelectedCount = ({ groupLocations }: { groupLocations: string[] }) => {
  const { inFlightLocationIds } = useLocationPicker(['inFlightLocationIds']);

  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);

  const count = groupLocations.filter((id) => inFlightSet.has(id)).length;

  if (count === 0) return null;

  return (
    <Text
      size='small'
      css={css`
        padding: ${theme.spacing(0, 0.5)};
        min-width: 16px;
        text-align: center;
        background: ${theme.colors.primary10};
        color: ${theme.colors.primary70};
        border-radius: ${theme.borderRadius.small};
        position: relative;
      `}
    >
      {count}
    </Text>
  );
};

type SelectDeselectProps = {
  selectedGroupLocations: string[];
  groupLocations: string[];
  groupId: string;
  allowCrossGroupSelection?: boolean;
};

const SelectDeselect = ({
  selectedGroupLocations,
  groupLocations,
  groupId,
  allowCrossGroupSelection = false,
}: SelectDeselectProps) => {
  const { t } = useTranslation();
  const {
    setInFlightGroupId,
    setInFlightLocationIds,
    inFlightLocationIds,
    organizationViewId,
    setInFlightOrganizationId,
  } = useLocationPicker([
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'inFlightLocationIds',
    'organizationViewId',
    'setInFlightOrganizationId',
  ]);

  const groupSet = useMemo(() => new Set(groupLocations), [groupLocations]);

  const handleDeselectAll = useCallback(() => {
    const nextLocations = allowCrossGroupSelection ? inFlightLocationIds.filter((id) => !groupSet.has(id)) : [];
    setInFlightLocationIds(nextLocations);
    setInFlightGroupId(allowCrossGroupSelection ? '' : nextLocations.length === 0 ? '' : groupId);
  }, [allowCrossGroupSelection, groupId, groupSet, inFlightLocationIds, setInFlightGroupId, setInFlightLocationIds]);

  const handleSelectAll = useCallback(() => {
    // Always replace with this group's locations — clears other parents in workspace mode
    setInFlightGroupId(groupId);
    setInFlightLocationIds(groupLocations);
    setInFlightOrganizationId(organizationViewId ?? '');
  }, [
    groupId,
    groupLocations,
    organizationViewId,
    setInFlightGroupId,
    setInFlightLocationIds,
    setInFlightOrganizationId,
  ]);

  if (selectedGroupLocations.length === groupLocations.length) {
    return (
      <Button
        onClick={handleDeselectAll}
        variant='tertiary'
        trackingId={LocationPickerTrackingIds.deselectAll('main-view')}
      >
        {t('Deselect All')}
      </Button>
    );
  }

  return (
    <Button onClick={handleSelectAll} variant='tertiary' trackingId={LocationPickerTrackingIds.selectAll('main-view')}>
      {t('Select All')}
    </Button>
  );
};
